import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Tabs, 
  Tab, 
  IconButton, 
  Tooltip, 
  ButtonGroup,
  Button,
  Typography
} from '@mui/material';
import Prism from 'prismjs';
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import { 
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  DesktopMac as WebViewIcon,
  PhoneAndroid as MobileViewIcon,
  Tablet as TabletIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

/**
 * Enhanced HTML Renderer component
 * Provides code/preview toggle and device mode preview
 */
const EnhancedHtmlRenderer = ({
  content = '',
  title = 'HTML Preview',
  showCode = false,
  onToggleCode,
  viewMode = 'web',
  onViewModeChange,
  onContentChange,
  onSave,
  onExport,
  onCopy,
  customStyles = {}
}) => {
  const [activeTab, setActiveTab] = useState(showCode ? 1 : 0);
  const [currentContent, setCurrentContent] = useState(content);
  const [isCopied, setIsCopied] = useState(false);
  const codeViewRef = useRef(null);
  const iframeRef = useRef(null);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (onToggleCode) {
      onToggleCode(newValue === 1);
    }
  };
  
  // Handle view mode change
  const handleViewModeChange = (mode) => {
    if (onViewModeChange) {
      onViewModeChange(mode);
    }
  };
  
  // Handle content copy
  const handleCopyContent = () => {
    if (onCopy) {
      onCopy();
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      return;
    }
    
    // Create enhanced content with injected JavaScript for copying
    let enhancedContent = injectScrollbarStyles(currentContent);
    enhancedContent = injectScreenNavigationScript(enhancedContent);
    
    navigator.clipboard.writeText(enhancedContent).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };
  
  // Apply Prism.js syntax highlighting when code content changes
  useEffect(() => {
    if (activeTab === 1 && codeViewRef.current) {
      Prism.highlightAllUnder(codeViewRef.current);
    }
  }, [activeTab, currentContent]);
  
  // Update content when props change
  useEffect(() => {
    setCurrentContent(content);
  }, [content]);
  
  // Update active tab when showCode prop changes
  useEffect(() => {
    setActiveTab(showCode ? 1 : 0);
  }, [showCode]);
  
  // Simple iframe load handler
  const handleIframeLoad = () => {
    console.log('Iframe loaded');
  };
  
  // Render toolbar
  const renderToolbar = () => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      p: 1,
      backgroundColor: '#f5f5f5',
      borderBottom: '1px solid #e0e0e0'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* View mode toggle buttons moved to left */}
        <ButtonGroup size="small" sx={{ mr: 3 }}>
          <Tooltip title="Desktop view">
            <Button 
              variant={viewMode === 'web' ? 'contained' : 'outlined'}
              onClick={() => handleViewModeChange('web')}
              sx={{ p: '4px 8px' }}
            >
              <WebViewIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Tablet view">
            <Button 
              variant={viewMode === 'tablet' ? 'contained' : 'outlined'}
              onClick={() => handleViewModeChange('tablet')}
              sx={{ p: '4px 8px' }}
            >
              <TabletIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Mobile view">
            <Button 
              variant={viewMode === 'mobile' ? 'contained' : 'outlined'}
              onClick={() => handleViewModeChange('mobile')}
              sx={{ p: '4px 8px' }}
            >
              <MobileViewIcon fontSize="small" />
            </Button>
          </Tooltip>
        </ButtonGroup>
        
        {/* Code/Preview tabs */}
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ minHeight: '40px' }}
        >
          <Tab 
            icon={<VisibilityIcon fontSize="small" />} 
            iconPosition="start"
            label="Preview" 
            sx={{ minHeight: '40px', textTransform: 'none' }}
          />
          <Tab 
            icon={<CodeIcon fontSize="small" />} 
            iconPosition="start"
            label="Code" 
            sx={{ minHeight: '40px', textTransform: 'none' }}
          />
        </Tabs>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Export button */}
        <Tooltip title="Export HTML">
          <IconButton 
            size="small" 
            onClick={onExport}
            sx={{ color: '#485869' }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {/* Copy button */}
        <Tooltip title={isCopied ? "Copied!" : "Copy HTML"}>
          <IconButton 
            size="small" 
            onClick={handleCopyContent}
            sx={{ color: isCopied ? 'success.main' : '#485869' }}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
  
  // Inject scrollbar styles into HTML content
  const injectScrollbarStyles = (htmlContent) => {
    if (!htmlContent) return htmlContent;
    
    // Check if the content already has custom scrollbar styles
    if (htmlContent.includes('webkit-scrollbar')) {
      return htmlContent;
    }
    
    // Scrollbar styles to inject
    const scrollbarStyles = `
<style>
/* Custom Scrollbar Styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background-color: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background-color: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #a8a8a8;
}

/* Firefox scrollbar */
* {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}
</style>
`;
    
    // Inject the styles before the closing head tag or at the beginning
    if (htmlContent.includes('</head>')) {
      return htmlContent.replace('</head>', `${scrollbarStyles}</head>`);
    } else if (htmlContent.includes('<body>')) {
      return htmlContent.replace('<body>', `${scrollbarStyles}<body>`);
    } else {
      // Otherwise prepend to the content
      return scrollbarStyles + htmlContent;
    }
  };

  // Inject screen navigation script into HTML content
  const injectScreenNavigationScript = (htmlContent) => {
    if (!htmlContent) return htmlContent;
    
    // Check if the content already has the navigation script
    if (htmlContent.includes('function showScreen')) {
      return htmlContent;
    }
    
    // Navigation script to inject
    const navigationScript = `
<script>
// Screen navigation script
function showScreen(screenId) {
  // First hide all screens
  document.querySelectorAll('.screen').forEach(screen => {
    screen.style.display = 'none';
  });
  
  // Then show the selected screen
  const screenToShow = document.getElementById(screenId);
  if (screenToShow) {
    screenToShow.style.display = 'block';
    console.log('Showing screen:', screenId);
  } else {
    console.error('Screen not found:', screenId);
  }
  
  // Update active state in navigation
  document.querySelectorAll('.screen-nav-item').forEach(item => {
    item.classList.remove('active', 'bg-gray-200', 'font-semibold');
    item.classList.add('bg-gray-100');
  });
  
  // Find and update the active navigation item
  const activeNavItem = document.querySelector(\`.screen-nav-item[data-screen="\${screenId}"]\`);
  if (activeNavItem) {
    activeNavItem.classList.add('active', 'bg-gray-200', 'font-semibold');
    activeNavItem.classList.remove('bg-gray-100');
  }
}

// Initialize the first screen on load
document.addEventListener('DOMContentLoaded', function() {
  // Show the first screen by default
  const firstScreen = document.querySelector('.screen');
  if (firstScreen) {
    showScreen(firstScreen.id);
  }
  
  // Add click handlers to all navigation items
  document.querySelectorAll('.screen-nav-item').forEach(item => {
    const screenId = item.getAttribute('data-screen');
    item.addEventListener('click', function(e) {
      e.preventDefault();
      showScreen(screenId);
    });
  });
});
</script>
`;
    
    // Inject the script before the closing body tag
    if (htmlContent.includes('</body>')) {
      return htmlContent.replace('</body>', `${navigationScript}</body>`);
    } else if (htmlContent.includes('</html>')) {
      // If no body tag but has html tag, add before closing html
      return htmlContent.replace('</html>', `${navigationScript}</html>`);
    } else {
      // Otherwise just append to the end
      return htmlContent + navigationScript;
    }
  };

  // Render preview content based on view mode
  const renderPreview = () => {
    // Inject scrollbar styles and navigation script into the content
    let enhancedContent = injectScrollbarStyles(currentContent);
    enhancedContent = injectScreenNavigationScript(enhancedContent);
    
    const iframeProps = {
      ref: iframeRef,
      srcDoc: enhancedContent,
      title: title,
      style: {
        width: '100%',
        height: '100%',
        border: 'none',
        backgroundColor: 'white'
      },
      sandbox: "allow-scripts allow-same-origin",
      loading: "lazy",
      onLoad: handleIframeLoad
    };
    
    if (viewMode === 'web') {
      // Desktop view - full width with increased height
      return (
        <Box sx={{ 
          width: '100%', 
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <iframe {...iframeProps} style={{...iframeProps.style, height: '100%'}} />
        </Box>
      );
    } else if (viewMode === 'tablet') {
      // Tablet view
      return (
        <Box sx={{ 
          width: '100%', 
          height: '100%', 
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          p: 2
        }}>
          <Box sx={{ 
            width: '600px', // Reduced from 768px
            height: '800px', // Reduced from 1024px for better visibility
            border: '6px solid #333',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 0 10px rgba(0,0,0,0.15)',
            backgroundColor: 'white'
          }}>
            <iframe {...iframeProps} />
          </Box>
        </Box>
      );
    } else {
      // Mobile view
      return (
        <Box sx={{ 
          width: '100%', 
          height: '100%', 
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          p: 2
        }}>
          <Box sx={{ 
            width: '375px',
            height: '667px',
            border: '4px solid #333', // Reduced from 10px to 4px
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 0 10px rgba(0,0,0,0.15)', // Lighter shadow
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120px', // Slightly smaller notch
              height: '14px', // Reduced height
              backgroundColor: '#333',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
              zIndex: 10
            }
          }}>
            <iframe {...iframeProps} />
          </Box>
        </Box>
      );
    }
  };
  
  // Render code view
  const renderCodeView = () => (
    <Box 
      ref={codeViewRef}
      sx={{ 
        width: '100%', 
        height: '100%',
        overflow: 'auto',
        padding: 2,
        backgroundColor: '#f8f8f8',
        fontFamily: 'monospace',
        fontSize: '14px',
        whiteSpace: 'pre-wrap'
      }}
    >
      <pre style={{ margin: 0 }}>
        <code className="language-html">
          {currentContent}
        </code>
      </pre>
    </Box>
  );
  
  return (
    <Paper 
      elevation={1} 
      sx={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 0,
        ...customStyles
      }}
    >
      {renderToolbar()}
      
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {activeTab === 0 ? renderPreview() : renderCodeView()}
      </Box>
    </Paper>
  );
};

export default EnhancedHtmlRenderer;